/**
 * Single source of truth for the Onboarding step order and the URL
 * segment that maps to each step. Used by:
 *  - OnboardingShell to render the Stepper
 *  - useUrlStep to validate the URL segment
 *  - useStepNavigation to resolve goToNext / goToPrev
 *  - identity-routes.tsx to register the nested children
 *
 * The id values intentionally mirror the legacy SessionStep union so the
 * existing useUnifiedSessionStore, analytics events and backend mapping
 * helpers (mapBackendStepToFrontend) keep working without translation.
 */

export const ONBOARDING_BASE_PATH = '/onboarding' as const;

// Plan 09 namespace refactor (2026-05-20) + Sprint A (2026-05-20):
//   /register/* → 6 steps que rendea RegisterPage (5-cards layout)
//   /kyc/*      → 2 steps de KYC capture (JAAK SDK) — Sprint A los movió
//                  de /register a /kyc.
//   /onboarding/* → el resto (Shell steps: data/address/curp/rfc/profile/closing)
// Estas listas deben mantenerse en sync con identity-routes.tsx.
const REGISTER_NAMESPACE_STEP_IDS = new Set([
  'email',
  'email_verification',
  'phone',
  'phone_verification',
  'password',
  'location_and_camera',
]);

const KYC_NAMESPACE_STEP_IDS = new Set(['ine_capture', 'selfie', 'identity_result']);

/**
 * Devuelve el base path correcto para un step según su namespace:
 *  - `/kyc`      para steps de captura JAAK (ine_capture, selfie)
 *  - `/register` para steps que rendea RegisterPage (registration + location)
 *  - `/onboarding` para el resto (Shell steps: data, address, curp, rfc, profile, closing)
 */
export function getBasePathForStep(stepId: string): string {
  if (KYC_NAMESPACE_STEP_IDS.has(stepId)) return '/kyc';
  if (REGISTER_NAMESPACE_STEP_IDS.has(stepId)) return '/register';
  return ONBOARDING_BASE_PATH;
}

/**
 * Construye la URL completa de un step usando el namespace correcto.
 * Reemplaza el patrón `${ONBOARDING_BASE_PATH}/${ONBOARDING_STEP_PATHS[id]}`
 * que asumía un solo namespace.
 */
export function urlForOnboardingStep(stepId: string): string {
  const slug = ONBOARDING_STEP_PATHS[stepId as keyof typeof ONBOARDING_STEP_PATHS];
  return `${getBasePathForStep(stepId)}/${slug}`;
}

/**
 * Steps de persona física. Este es el flujo soportado en producción hoy.
 * Persona moral comparte la primera mitad (data / address) pero agregará
 * sus propios steps regulatorios (legal-rep, shareholders, etc.) cuando
 * el BE los exponga. Mantenemos un solo array por tipo para que el shell
 * sea agnóstico a la rama: pide `getOnboardingStepsFor(personType)` y
 * trabaja con esa lista.
 */
export const ONBOARDING_STEPS_INDIVIDUAL = [
  // bloque registration (BE)
  'email',
  'email_verification',
  'phone',
  'phone_verification',
  'password',
  // bloque kyc — captura (BE)
  'location_and_camera',
  'ine_capture',
  'selfie',
  // bloque kyc — review (FE-only, plan 09 F2)
  'identity_result',
  // FE-only review (colapsan al BE step kyc.data_confirmation;
  // ver `Decisión documentada: review-steps FE-only` en plan 07)
  'data',
  'address',
  'curp',
  'rfc',
  // bloque profile (BE)
  'transactional_profile',
  'fiscal_document',
  // bloque closing (BE)
  'beneficiary',
  'contract',
] as const;

/**
 * Stub de los steps de persona moral. NO usar todavía — la lista real
 * la define el BE cuando los endpoints existan. Vive aquí para que el
 * tipado y el shell ya estén preparados sin migración tipo big-bang.
 */
export const ONBOARDING_STEPS_BUSINESS = [
  // bloque registration (BE)
  'email',
  'email_verification',
  'phone',
  'phone_verification',
  'password',
  // bloque kyc — captura (BE)
  'location_and_camera',
  'ine_capture',
  'selfie',
  // bloque kyc — review (FE-only, plan 09 F2)
  'identity_result',
  // FE-only review
  'data',
  'address',
  'curp', // RFC del representante legal
  'rfc', // RFC de la empresa
  // bloque profile (BE)
  'transactional_profile',
  'fiscal_document',
  // bloque closing (BE)
  'beneficiary',
  'contract',
] as const;

/** Default (compat): el flujo actual de producción es persona física. */
export const ONBOARDING_STEPS = ONBOARDING_STEPS_INDIVIDUAL;

export type OnboardingStepId = (typeof ONBOARDING_STEPS_INDIVIDUAL)[number];

export type PersonTypeFlow = 'individual' | 'business';

/**
 * Devuelve la lista de steps que aplica para el tipo de persona dado.
 * Hoy ambos tipos comparten la misma secuencia: cuando persona moral
 * tenga sus propios endpoints, este es el único cambio necesario.
 */
export function getOnboardingStepsFor(personType: PersonTypeFlow): readonly OnboardingStepId[] {
  return personType === 'business' ? ONBOARDING_STEPS_BUSINESS : ONBOARDING_STEPS_INDIVIDUAL;
}

/**
 * URL segment used in the route path. Most ids are kebab-case-equivalent
 * (we use snake_case for ids to match the existing union type, but URLs
 * use hyphens as is web convention).
 */
export const ONBOARDING_STEP_PATHS: Record<OnboardingStepId, string> = {
  // bloque registration
  email: 'email',
  email_verification: 'email-verification',
  phone: 'phone',
  phone_verification: 'phone-verification',
  password: 'password',
  // bloque kyc — captura
  location_and_camera: 'location-and-camera',
  ine_capture: 'ine-capture',
  selfie: 'selfie',
  // bloque kyc — review (plan 09 F2)
  identity_result: 'identity-result',
  // FE-only review
  data: 'data',
  address: 'address',
  curp: 'curp',
  rfc: 'rfc',
  // bloque profile
  transactional_profile: 'transactional-profile',
  fiscal_document: 'fiscal-document',
  // bloque closing
  beneficiary: 'beneficiary',
  contract: 'contract',
};

/** Reverse lookup: URL segment → step id. */
export const ONBOARDING_PATH_TO_STEP: Record<string, OnboardingStepId> = Object.fromEntries(
  Object.entries(ONBOARDING_STEP_PATHS).map(([id, path]) => [path, id as OnboardingStepId])
) as Record<string, OnboardingStepId>;

/** Allowed URL segments, in display order. Suitable for useUrlStep. */
export const ONBOARDING_STEP_SEGMENTS: readonly string[] = ONBOARDING_STEPS.map(
  s => ONBOARDING_STEP_PATHS[s]
);

/** Numeric index per step id. Consumed by `OnboardingShell` to drive the
 * stepper position and by `resolveTargetStep` for "backend says step N
 * but FE-only steps come after" comparisons. */
export const STEP_INDEX: Record<string, number> = Object.fromEntries(
  ONBOARDING_STEPS.map((id, idx) => [id, idx])
);

type BackendNextStep = { block: string; step: string };
type Blocks = Record<string, { status: string; steps: Record<string, { status: string }> }>;

/**
 * Maps the backend `next_step` ({ block, step }) shape to a frontend step id.
 * Returns null when the combination is unknown (caller should fall back).
 */
export function mapBackendStepToFrontend(nextStep: BackendNextStep): OnboardingStepId | null {
  const stepMap: Record<string, Record<string, OnboardingStepId>> = {
    // Plan 07: bloque registration. El FE expone cada step BE como una URL
    // propia (`/onboarding/email`, `/onboarding/email-verification`, …).
    registration: {
      email: 'email',
      'email-verification': 'email_verification',
      email_verification: 'email_verification',
      phone: 'phone',
      'phone-verification': 'phone_verification',
      phone_verification: 'phone_verification',
      password: 'password',
    },
    kyc: {
      // Plan 09 F2 completó la migración: cada sub-step KYC tiene URL propia
      // bajo `/kyc/*` (`/kyc/ine-capture`, `/kyc/selfie`, `/kyc/identity-result`).
      'location-and-camera': 'location_and_camera',
      location_and_camera: 'location_and_camera',
      'ine-capture': 'ine_capture',
      ine_capture: 'ine_capture',
      selfie: 'selfie',
      // `kyc_processing` es sub-estado de selfie — el "verificando" post-POST
      // selfie. Recovery aterriza ahí mientras BE termina el match biométrico.
      kyc_processing: 'selfie',
      // Existentes (FE-only review collapse — ver `Decisión documentada` plan 07).
      'confirm-data': 'data',
      data_confirmation: 'data',
      'proof-of-address': 'address',
      proof_of_address: 'address',
      'extracted-data': 'curp',
      extracted_data: 'curp',
    },
    profile: {
      // Plan 07 F4 C.d: el step BE `profile.fiscal_document` ahora tiene
      // su propia URL (`/onboarding/fiscal-document`) gracias a F2.c
      // (FiscalDocumentStep) + F4 C.a (route registrada). El FE `rfc`
      // sobrevive como review step FE-only y queda accesible para
      // navegación lateral / browser back, pero el BE ya nunca dirige
      // allí — siempre apunta al step real.
      'fiscal-document': 'fiscal_document',
      fiscal_document: 'fiscal_document',
      'transactional-profile': 'transactional_profile',
      transactional_profile: 'transactional_profile',
      beneficiary: 'beneficiary',
      beneficiaries: 'beneficiary',
    },
    closing: {
      beneficiary: 'beneficiary',
      beneficiaries: 'beneficiary',
      contracts: 'contract',
      contracts_and_signature: 'contract',
    },
  };

  return stepMap[nextStep.block]?.[nextStep.step] ?? null;
}

/**
 * Given a backend-resolved target step, decide where the user should actually
 * land — accounting for frontend-only intermediate steps (address, curp, rfc)
 * that the backend doesn't model directly.
 */
export function resolveTargetStep(
  backendTarget: OnboardingStepId,
  blocks: Blocks | undefined
): OnboardingStepId {
  // El "address-redirect" sólo aplica a curp/rfc — son los únicos FE-only
  // review steps que dependen de que el bloque profile haya arrancado.
  // Cualquier otro step (registration/kyc-capture/data/address/profile/closing)
  // pasa directo. Antes este check era `backendTargetIndex <= 1`, que asumía
  // que data/address eran los dos primeros del array; tras plan 07 F1 esa
  // posición la ocupan los steps de registration y el shortcut numérico
  // dejaría de ser correcto.
  if (backendTarget !== 'curp' && backendTarget !== 'rfc') return backendTarget;

  if (!blocks) return backendTarget;

  // address/curp/rfc are frontend-only. They are considered done once the
  // profile block has started or finished server-side.
  const profileStatus = blocks.profile?.status;
  const frontendOnlyCompleted = profileStatus === 'in_progress' || profileStatus === 'completed';

  if (!frontendOnlyCompleted) {
    return 'address';
  }
  return backendTarget;
}

/**
 * Returns true when the given step has been completed server-side and
 * therefore should NOT be revisited (or rendered as clickable in the
 * stepper).
 */
export function isStepLocked(stepId: OnboardingStepId, blocks: Blocks | undefined): boolean {
  if (!blocks) return false;

  // Plan 07: los steps nuevos (registration: email/email_verification/phone/
  // phone_verification/password; kyc capture: location_and_camera/ine_capture/
  // selfie; profile: fiscal_document) NO se bloquean al completarse. Razón:
  // todos son editables — el usuario puede presionar browser back o el
  // botón "Back" del Shell para regresar y corregir su input. Bloquearlos
  // hacía que el guard rebote hacia adelante (bug que C.c.fix resolvió).
  //
  // El BE valida la edición al recibir la re-submission (e.g., email
  // verificado no permite cambiar a otro sin re-OTP). El bloqueo runtime
  // pertenece a esa capa, no al guard de routing.
  //
  // Excepción documentada: `contract` post-firma SÍ es irreversible — eso
  // lo cubre canGoBackOnStep + useBlocker (plan 01 F6), no isStepLocked.

  const kycDataStatus = blocks.kyc?.steps?.data_confirmation?.status;
  const proofOfAddressStatus = blocks.kyc?.steps?.proof_of_address?.status;
  const transactionalProfileStatus = blocks.profile?.steps?.transactional_profile?.status;

  if (stepId === 'data' && kycDataStatus === 'completed') return true;
  if (
    stepId === 'address' &&
    (proofOfAddressStatus === 'completed' || transactionalProfileStatus === 'completed')
  )
    return true;
  if (stepId === 'transactional_profile' && transactionalProfileStatus === 'completed') return true;
  if ((stepId === 'curp' || stepId === 'rfc') && transactionalProfileStatus === 'completed')
    return true;

  const beneficiariesStatus = blocks.closing?.steps?.beneficiaries?.status;
  if (stepId === 'beneficiary' && beneficiariesStatus === 'completed') return true;

  return false;
}

/**
 * Derives the list of step ids the user is allowed to be on right now.
 * "Unlocked" means: every step at index ≤ the backend's next_step index,
 * minus those that are explicitly locked by isStepLocked (already done).
 *
 * For the very first paint while the session query is still loading, fall
 * back to "everything unlocked" so the guard does not redirect prematurely.
 */
export function deriveUnlockedSteps(
  backendNextStep: BackendNextStep | null | undefined,
  blocks: Blocks | undefined
): readonly OnboardingStepId[] {
  if (!backendNextStep) return ONBOARDING_STEPS;

  const mapped = mapBackendStepToFrontend(backendNextStep);
  if (!mapped) return ONBOARDING_STEPS;
  const target = resolveTargetStep(mapped, blocks);
  // BE `data_confirmation` describes the next BE call, not the FE view to
  // pin the user on: the FE only wires /kyc/confirm-data at the curp step,
  // after collecting data + address + curp. Treat the three FE-only review
  // steps as a single unlocked block — otherwise the guard pins the user at
  // /data with no way to reach the step that actually fires the BE call.
  const ceiling: OnboardingStepId = target === 'data' ? 'curp' : target;
  const ceilingIndex = STEP_INDEX[ceiling] ?? 0;

  // Todos los steps hasta el ceiling quedan desbloqueados (navegables), INCLUSO
  // los ya completados: el usuario debe poder retroceder con VOLVER o el stepper
  // a un step previo para revisarlo/editarlo. Antes se excluían los completados
  // (isStepLocked), lo que hacía que goToPrev los saltara hasta el último KYC
  // no-locked (identity-result) y que el stepper ignorara sus clicks. El guard
  // no rebota porque currentStep siempre queda ≤ ceiling. La irreversibilidad
  // real del contrato post-firma la cubre canGoBackOnStep + useBlocker.
  return ONBOARDING_STEPS.filter((_id, idx) => idx <= ceilingIndex);
}

/**
 * Derives the list of step ids that are considered already completed by
 * the backend (every step strictly before the backend's current target).
 */
export function deriveCompletedSteps(
  backendNextStep: BackendNextStep | null | undefined,
  blocks: Blocks | undefined
): readonly OnboardingStepId[] {
  if (!backendNextStep) return [];
  const mapped = mapBackendStepToFrontend(backendNextStep);
  if (!mapped) return [];
  const target = resolveTargetStep(mapped, blocks);
  const targetIndex = STEP_INDEX[target] ?? 0;
  return ONBOARDING_STEPS.filter((_id, idx) => idx < targetIndex);
}

/**
 * Per-step "can the user go back from here?" check.
 *
 * Plan 01 (F4) requires `canGoBack` so steps with irreversible side
 * effects can hide / disable the Back button. Today only `contract` in
 * `continueAfterSign` mode qualifies (the user has already e-signed,
 * going back would risk a double-signature flow).
 *
 * Pure to keep the gating contract testable without React. The Shell
 * passes the runtime bits it owns (currently `contractButtonMode`).
 * Add fields here as new irreversible states surface — never sprinkle
 * the conditions across components.
 */
export type CanGoBackRuntime = {
  /** Current state of the contract step's primary button. */
  contractButtonMode?: 'sign' | 'accept' | 'continueAfterSign';
};

export function canGoBackOnStep(stepId: OnboardingStepId, runtime: CanGoBackRuntime = {}): boolean {
  if (stepId === 'contract' && runtime.contractButtonMode === 'continueAfterSign') {
    return false;
  }
  return true;
}
